import { Component, AsyncComponent } from 'vue';

import type { SnowplowContext } from '@/helpers/tracking/providers';

export interface VFMPayload {
	type?: 'modal' | 'alert' | 'toast' | 'popover' | 'action-sheet' | 'menu';
	props?: any;
	onOpened?: Function;
	onBeforeClosed?: Function;
	onClosed?: Function;
	slotComponent: any;
	slotProps?: any;
	slotId?: string;
	onSlot?: any;
	[key: string]: any;
}

// alert-helper.ts
export interface AlertButton {
	text: string;
	role?: 'cancel' | 'destructive' | string;
	cssClass?: string | string[];
	handler?: (value?: any) =>
		| boolean
		| void
		| {
				[key: string]: any;
		  };
}

export interface AlertInput {
	type?:
		| 'date'
		| 'email'
		| 'number'
		| 'password'
		| 'search'
		| 'tel'
		| 'text'
		| 'url'
		| 'time'
		| 'week'
		| 'month'
		| 'datetime-local'
		| 'checkbox'
		| 'radio'
		| 'textarea';
	name?: string;
	placeholder?: string;
	value?: any;
	label?: string;
	checked?: boolean;
	disabled?: boolean;
	id?: string;
	handler?: (input: AlertInput) => void;
	min?: string | number;
	max?: string | number;
	cssClass?: string | string[];
	attributes?: any;
	tabindex?: number;
}

export interface ShowAlertPayload {
	header: string;
	subHeader?: string;
	message?: string;
	buttons: string[];
	options?: Object;
	additionalContexts?: SnowplowContext[];
}

export interface ShowPromptPayload {
	header: string;
	subHeader?: string;
	message?: string;
	buttons: AlertButton[];
	options?: Object;
	inputs?: AlertInput[];
}

// popover-helper.ts
type ComponentProps = { [key: string]: any };
type ComponentRef = string | Component | AsyncComponent;
export interface PopoverOptions<T extends ComponentRef = ComponentRef> extends ComponentProps {
	popoverProps?: ComponentProps;
	componentProps?: ComponentProps;
	component: T;
	onSlot?: Record<string, (data?: any) => void>;
}

// toast-helper.ts
export interface ToastCallback {
	message: string;
	callback: Function;
}
export interface ToastButton {
	text?: string;
	icon?: string;
	side?: 'start' | 'end';
	role?: 'cancel' | string;
	cssClass?: string | string[];
	handler?: () => boolean | void | Promise<boolean | void>;
}

export enum ToastIconSide {
	START = 'start',
	END = 'end',
}

export type ToastColors =
	| 'primary'
	| 'secondary'
	| 'tertiary'
	| 'success'
	| 'warning'
	| 'danger'
	| 'light'
	| 'medium'
	| 'dark';

export type ToastPosition = 'bottom' | 'middle' | 'top';
export type ToastAction = 'add' | 'remove';

export interface ShowToastPayload {
	message: string;
	duration?: number;
	buttons?: ToastButton[];
	position?: ToastPosition;
	color?: ToastColors;
	cssClass?: string;
}

export interface ToastCallback {
	message: string;
	callback: Function;
}

// action-sheet-helper.ts
export type Mode = 'ios' | 'md';
export interface ActionSheetButton {
	text?: string;
	role?: 'cancel' | 'destructive' | 'selected' | string;
	icon?: string;
	cssClass?: string | string[];
	handler?: () => boolean | void | Promise<boolean | void>;
}
export declare type AnimationBuilder = (baseEl: any, opts?: any) => Animation;

export interface ActionSheetOptions {
	header?: string;
	subHeader?: string;
	cssClass?: string | string[];
	buttons: (ActionSheetButton | string)[];
	backdropDismiss?: boolean;
	translucent?: boolean;
	animated?: boolean;
	mode?: Mode;
	keyboardClose?: boolean;
	id?: string;
	htmlAttributes?: any;
	enterAnimation?: AnimationBuilder;
	leaveAnimation?: AnimationBuilder;
}

export interface OverlayController {
	create(opts?: any): Promise<HTMLElement>;
	dismiss(data?: any, role?: string, id?: string): Promise<boolean>;
	getTop(): Promise<any | undefined>;
}
